// GeneratePromptModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles
import placeholders from '../SelectPlaceholders.json';

// Bind modal to app element for accessibility (usually #root or #__next)
// Make sure your main app root element has this ID, or adjust accordingly.
if (typeof window !== 'undefined') {
  Modal.setAppElement('#root');
}

function GeneratePromptModal({ isOpen, onClose, promptText, loading, error, isFreeTier, adLink }) {
  const [editorContent, setEditorContent] = useState('');
  const [isPreviewVisible, setIsPreviewVisible] = useState(true);
  const quillRef = useRef(null);

  useEffect(() => {
    if (loading) {
      setEditorContent('Generating...');
      console.log('Modal: Loading state, setting content to "Generating..."');
    } else if (error) {
      setEditorContent(`Error: ${error}`);
      console.log('Modal: Error state, setting content to error message:', error);
    } else {
      setEditorContent(promptText || '');
      console.log('Modal: Setting content to promptText:', promptText);
    }
  }, [promptText, loading, error]);

  useEffect(() => {
    if (isFreeTier && adLink) {
      // Open the ad in a new tab when Generate Prompt is clicked in free tier
      window.open(adLink, '_blank');
    }
  }, [isFreeTier, adLink]);

  const handleClose = () => {
    onClose(); // This will be responsible for clearing data in App.js too
  };

  const handleSave = () => {
    const contentToSave = quillRef.current ? quillRef.current.getEditor().getText() : editorContent;
    if (!contentToSave || loading || error) {
        alert("No content to save or still loading/error.");
        return;
    }
    const blob = new Blob([contentToSave], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'generated_prompt.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopy = () => {
    if (loading || error || !quillRef.current) {
      alert("Cannot copy now.");
      return;
    }
    const plainText = quillRef.current.getEditor().getText();
    navigator.clipboard.writeText(plainText).then(() => {
      alert('Prompt copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      alert('Failed to copy prompt.');
    });
  };

  const togglePreview = () => setIsPreviewVisible(!isPreviewVisible);

  const insertPlaceholder = (placeholder) => {
    if (!quillRef.current) return;
    
    const editor = quillRef.current.getEditor();
    const range = editor.getSelection() || { index: 0, length: 0 };
    editor.insertText(range.index, placeholder);
    editor.setSelection(range.index + placeholder.length);
    editor.focus();
  };

  const modules = {
    toolbar: false // Disable the toolbar completely for plain text editing
  };

  // Function to highlight placeholders in blue
  const highlightPlaceholders = (text) => {
    if (!text) return [];
    
    const placeholderPattern = /\{\{(\w+)\}\}/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = placeholderPattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      parts.push(
        <span key={match.index} className="bg-blue-100 text-blue-800 px-1 rounded">
          {match[0]}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts;
  };

  // Get plain text for preview if editor is available
  const previewText = quillRef.current ? quillRef.current.getEditor().getText() : editorContent;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      overlayClassName="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex justify-center items-center z-50"
      className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-11/12 md:w-3/4 lg:w-1/2 outline-none"
      contentLabel="Generated Prompt Modal"
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-gray-800">Generated Prompt</h2>
        <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
      </div>

      {loading && <div className="text-center py-4 text-blue-600">Generating prompt...</div>}
      {error && !loading && <div className="text-center py-4 text-red-600">Error: {error}</div>}
      
      {!loading && !error && (
        <>
          <div className="mb-4">
            <div className="mb-2 flex justify-between items-center">
              <div className="flex items-center">
                <span className="text-gray-700 mr-2">Insert placeholder:</span>
                <select
                  className="p-1 border rounded focus:outline-none focus:ring-2 focus:ring-[#0078D4] text-sm"
                  onChange={(e) => {
                    if (e.target.value) {
                      insertPlaceholder(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  defaultValue=""
                  style={{ maxHeight: '2.25rem' }}
                >
                  <option value="" disabled>Select placeholder</option>
                  {placeholders.availablePlaceholders.map((placeholder) => (
                    <option key={placeholder.name} value={placeholder.name} className="text-sm">
                      {placeholder.name} - {placeholder.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="h-64 overflow-y-auto border border-gray-300 rounded-md">
              <ReactQuill
                ref={quillRef}
                value={editorContent}
                onChange={setEditorContent}
                modules={modules}
                theme="snow"
                className="h-full"
                style={{ border: 'none' }}
              />
            </div>
          </div>

          <div className="mt-4 border-t pt-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold text-gray-700">Preview</h3>
              <button onClick={togglePreview} className="text-sm text-blue-600 hover:text-blue-800">
                {isPreviewVisible ? 'Hide' : 'Show'} Preview
              </button>
            </div>
            {isPreviewVisible && (
              <div className="mb-4 p-4 border border-gray-300 rounded-lg bg-gray-50 relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Preview</label>
                <pre className="whitespace-pre-wrap break-words text-sm text-gray-900 bg-white p-2 border rounded max-h-48 overflow-y-auto">
                  {highlightPlaceholders(previewText || 'N/A')}
                </pre>
              </div>
            )}
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={handleCopy}
          disabled={loading || error || !editorContent}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          Copy
        </button>
        <button
          onClick={handleSave}
          disabled={loading || error || !editorContent}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
        >
          Save
        </button>
        <button
          onClick={handleClose}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
        >
          Close
        </button>
      </div>

      {isFreeTier && (
        <div className="mt-4 p-3 bg-gray-100 rounded-lg text-sm">
          <p>💡 <strong>Pro Tip:</strong> Upgrade to our premium tier for an ad-free experience and advanced features!</p>
        </div>
      )}
    </Modal>
  );
}

export default GeneratePromptModal;